import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Crown, Menu, Shield, MessageSquare, Home, Trash2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import OwlLogo from "./OwlLogo";

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="border-b-2 border-[#0F1120]" style={{background:"linear-gradient(135deg,#1A0F46 0%,#2C166C 40%,#5B21AC 70%,#075985 100%)"}}>
      <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group min-w-0 flex-shrink">
          <OwlLogo size={44} />
          <div className="min-w-0 leading-none">
            <div className="font-black tracking-tight flex flex-col sm:flex-row sm:items-baseline sm:gap-1">
              <span className="text-sm sm:text-xl leading-tight">
                <span className="text-[#FFD93D]">Brimi</span><span className="text-white">AI</span>
                <span className="text-white/50 mx-1 hidden sm:inline">·</span>
              </span>
              <span className="text-white text-[11px] sm:text-xl leading-tight">ScienceLab</span>
            </div>
            <div className="hidden sm:block text-[10px] tracking-[0.2em] uppercase font-bold text-[#64C3F5] mt-1">ICSE Class IX & X</div>
          </div>
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-3">
          {user ? (
            <>
              <Link to="/dashboard" className="hidden sm:inline-block">
                <Button variant="outline" className="brut-btn rounded-none bg-[#FFF1B8] text-[#0F1120] hover:bg-[#FFF1B8]" data-testid="nav-dashboard">
                  Dashboard
                </Button>
              </Link>
              {user.role === "owner" && (
                <Link to="/owner">
                  <Button className="brut-btn rounded-none bg-[#FF6B7A] text-white hover:bg-[#FF6B7A]" data-testid="nav-owner">
                    <Crown size={16} className="mr-1" strokeWidth={2.5}/>Owner
                  </Button>
                </Link>
              )}
              <span className="hidden md:block text-xs tracking-[0.2em] uppercase font-bold text-white/80">
                {user.name}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    data-testid="header-menu-btn"
                    aria-label="Open menu"
                    className="brut-btn rounded-none bg-white text-[#0F1120] hover:bg-white">
                    <Menu size={16} strokeWidth={2.5}/>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="brut-card rounded-none border-2 border-[#0F1120] bg-white min-w-[220px] p-1"
                  data-testid="header-menu">
                  <DropdownMenuLabel className="text-[10px] font-black tracking-[0.25em] uppercase text-neutral-500">
                    {user.name}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild data-testid="menu-dashboard">
                    <Link to="/dashboard" className="cursor-pointer">
                      <Home size={16} className="mr-2" strokeWidth={2.5}/> Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="menu-feedback"
                    onSelect={() => document.querySelector('[data-testid="open-feedback-btn"]')?.click()}
                    className="cursor-pointer">
                    <MessageSquare size={16} className="mr-2" strokeWidth={2.5}/> Rate &amp; Feedback
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-privacy">
                    <Link to="/privacy" className="cursor-pointer">
                      <Shield size={16} className="mr-2" strokeWidth={2.5}/> Privacy Policy
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild data-testid="menu-data-deletion">
                    <Link to="/data-deletion" className="cursor-pointer">
                      <Trash2 size={16} className="mr-2" strokeWidth={2.5}/> Delete My Data
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => { logout(); nav("/"); }}
                    data-testid="menu-logout"
                    className="cursor-pointer text-[#B4152C] focus:text-[#B4152C]">
                    <LogOut size={16} className="mr-2" strokeWidth={2.5}/> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="outline" className="brut-btn rounded-none bg-white text-[#0F1120] hover:bg-white" data-testid="nav-login">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="brut-btn rounded-none grad-cta hover:opacity-90" data-testid="nav-signup">
                  <span className="hidden sm:inline">Sign Up Free</span>
                  <span className="sm:hidden">Sign Up</span>
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
